/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { breakpoints } from '@coursera/cds-core';

import { LEARNER_HELP_CENTER_LINK } from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import Apple from 'bundles/authentication/shared/Apple';
import Facebook from 'bundles/authentication/shared/Facebook';
import Google from 'bundles/authentication/shared/Google';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';

import _t from 'i18n!nls/authentication';

type Props = {
  onError: (error: SSOError) => void;
  onSuccess: (isRegistration: boolean) => void;
  services?: ('apple' | 'facebook' | 'google')[];
  toFocus?: boolean;
};

type State = {
  error?: string;
};

const BUTTONS = {
  apple: Apple,
  facebook: Facebook,
  google: Google,
};

const HANDLED_ERRORS = ['unableToGetProfile', 'unknownError'];

function errorMessagesFactory() {
  return {
    unableToGetProfile: _t('Unable to get profile. Please try again.'),
    unknownError: (
      <FormattedMessage
        message={_t(
          'Something went wrong. Please refresh the page and try again. If the problem persists, visit the {helpCenterLink}.'
        )}
        helpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</a>}
      />
    ),
  };
}

const styles = {
  container: css`
    display: grid;
    grid-gap: 16px;
    -ms-grid-columns: 1fr;
  `,
  button: css`
    :nth-of-type(1) {
      -ms-grid-row: 1;
    }

    :nth-of-type(2) {
      -ms-grid-row: 2;
    }

    :nth-of-type(3) {
      -ms-grid-row: 3;
    }
  `,
  horizontalButtonStyles: css`
    display: flex;
    justify-content: center;
    align-items: center;
    position: unset;

    ${breakpoints.down('xs')} {
      padding: 8px 12px;
    }

    svg {
      margin-right: 8px;
      left: unset;
      position: unset;
      top: unset;

      ${breakpoints.down('xs')} {
        margin-right: 4px;
      }
    }
  `,
};

class SocialSSO extends React.Component<Props, State> {
  state: State = {};

  firstButtonRef = React.createRef<HTMLButtonElement>();

  handleError = (error: SSOError) => {
    const { onError = () => {} } = this.props;

    if (HANDLED_ERRORS.includes(error.code)) {
      // only handle close of the modal or click in reject app
      const errorMessages = errorMessagesFactory();
      // @ts-expect-error TS7016 TODO: error do not exist in errorMessages
      this.setState({ error: errorMessages[error.code] });
    } else {
      onError(error);
    }
  };

  componentDidMount() {
    if (this.props.toFocus) {
      this.firstButtonRef.current?.focus();
    }
  }

  render() {
    const { onSuccess, services = ['google', 'facebook', 'apple'] } = this.props;
    const { error } = this.state;

    return (
      <section css={styles.container}>
        {error && <Alert>{error}</Alert>}
        {services.map((service, idx) => {
          const Button = BUTTONS[service];

          return (
            <Button
              css={styles.button}
              key={service}
              onError={this.handleError}
              onSuccess={onSuccess}
              {...(!idx ? { forwardedRef: this.firstButtonRef } : {})}
            />
          );
        })}
      </section>
    );
  }
}

export default SocialSSO;
